import React from 'react';
import { Box, Skeleton, Card, CardContent } from '@mui/material';

/**
 * Prayer Card Skeleton
 * Matches the PrayerCard component layout
 */
export function PrayerCardSkeleton({ variant = 'default' }) {
  return (
    <Card elevation={0}>
      <CardContent sx={{ pt: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Skeleton
            variant="circular"
            width={variant === 'compact' ? 36 : 44}
            height={variant === 'compact' ? 36 : 44}
            sx={{ mr: 1.5 }}
          />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="40%" height={20} />
            <Skeleton variant="text" width="25%" height={16} />
          </Box>
          <Skeleton variant="rounded" width={60} height={24} sx={{ borderRadius: 3 }} />
        </Box>

        {/* Content */}
        <Skeleton variant="text" width="100%" height={24} />
        <Skeleton variant="text" width="95%" height={24} />
        <Skeleton variant="text" width="70%" height={24} />

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="circular" width={28} height={28} />
        </Box>
      </CardContent>
    </Card>
  );
}

/**
 * List of Prayer Card Skeletons
 */
export function PrayerListSkeleton({ count = 3, variant = 'default' }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {Array.from({ length: count }).map((_, index) => (
        <PrayerCardSkeleton key={index} variant={variant} />
      ))}
    </Box>
  );
}

/**
 * Circle Card Skeleton
 */
export function CircleCardSkeleton() {
  return (
    <Card elevation={0}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 1.5 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="60%" height={24} />
          </Box>
          <Skeleton variant="circular" width={24} height={24} />
        </Box>
        <Skeleton variant="text" width="100%" height={18} />
        <Skeleton variant="text" width="80%" height={18} />
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: 3 }} />
          <Skeleton variant="rounded" width={100} height={24} sx={{ borderRadius: 3 }} />
        </Box>
      </CardContent>
    </Card>
  );
}

/**
 * Dashboard Stats Skeleton
 */
export function StatCardSkeleton() {
  return (
    <Card elevation={0}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Skeleton variant="text" width={100} height={20} />
            <Skeleton variant="text" width={60} height={40} />
          </Box>
          <Skeleton variant="circular" width={48} height={48} />
        </Box>
      </CardContent>
    </Card>
  );
}

/**
 * Profile Header Skeleton
 */
export function ProfileHeaderSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
      <Skeleton variant="circular" width={100} height={100} sx={{ mb: 2 }} />
      <Skeleton variant="text" width={150} height={32} />
      <Skeleton variant="text" width={200} height={20} />
      <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Skeleton variant="text" width={40} height={28} sx={{ mx: 'auto' }} />
          <Skeleton variant="text" width={60} height={16} sx={{ mx: 'auto' }} />
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Skeleton variant="text" width={40} height={28} sx={{ mx: 'auto' }} />
          <Skeleton variant="text" width={60} height={16} sx={{ mx: 'auto' }} />
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Skeleton variant="text" width={40} height={28} sx={{ mx: 'auto' }} />
          <Skeleton variant="text" width={60} height={16} sx={{ mx: 'auto' }} />
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Message List Skeleton
 */
export function MessageListSkeleton({ count = 5 }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {Array.from({ length: count }).map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            gap: 2,
          }}
        >
          <Skeleton variant="circular" width={48} height={48} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="50%" height={20} />
            <Skeleton variant="text" width="70%" height={16} />
          </Box>
          <Skeleton variant="text" width={40} height={14} />
        </Box>
      ))}
    </Box>
  );
}

/**
 * Page Loading Skeleton
 * Full page loading state
 */
export function PageLoadingSkeleton() {
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Skeleton variant="text" width={200} height={40} />
        <Skeleton variant="rounded" width={120} height={40} sx={{ borderRadius: 2 }} />
      </Box>

      {/* Stats row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
          gap: 2,
          mb: 4,
        }}
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <StatCardSkeleton key={i} />
        ))}
      </Box>

      {/* Content */}
      <PrayerListSkeleton count={3} />
    </Box>
  );
}

export default {
  PrayerCardSkeleton,
  PrayerListSkeleton,
  CircleCardSkeleton,
  StatCardSkeleton,
  ProfileHeaderSkeleton,
  MessageListSkeleton,
  PageLoadingSkeleton,
};
